import { extractApiErrorMessage, extractValidationErrors } from '../../../Utils/apiErrorUtils';
import type { EntityFormApiErrorExtractionResult } from '../../Shared/Hooks/useEntityForm.types';

const OFFER_ERROR_MESSAGES_BY_CODE: Record<string, string> = {
  OFFER_COMPANY_REQUIRED: 'Wybierz firmę dla oferty.',
  OFFER_COMPANY_INVALID: 'Wybierz poprawną firmę dla oferty.',
  OFFER_COMPANY_NOT_FOUND: 'Wybrana firma nie istnieje. Odśwież listę i wybierz ponownie.',
};

const OFFER_FIELD_LABELS: Record<string, string> = {
  name: 'Nazwa oferty',
  companyId: 'Firma',
  defaultInvoiceScheme: 'Domyślny schemat faktury',
  hotelId: 'Hotel',
  regionId: 'Region',
  countryId: 'Kraj',
  description: 'Opis',
  metaTitle: 'Tytuł meta',
  metaDescription: 'Opis meta',
  metaKeywords: 'Słowa kluczowe meta',
  priceIncludes: 'Cena zawiera',
  additionalPaid: 'Dodatkowo płatne',
  importantInformation: 'Ważne informacje',
  offerCategoryIds: 'Kategorie',
  offerTagIds: 'Tagi',
  'offerOptions.offerOptionsTypeId': 'Typ opcji oferty',
  'offerOptions.offerOptionsValueId': 'Wartość opcji oferty',
};

const GENERIC_VALIDATION_MESSAGE =
  'Formularz zawiera błędy. Popraw oznaczone pola i spróbuj ponownie.';
const GENERIC_SERVER_ERROR_MESSAGE =
  'Wystąpił nieoczekiwany błąd serwera. Spróbuj ponownie za chwilę.';

const normalizeApiFieldPath = (fieldPath: string): string => {
  return fieldPath
    .replace(/^\$?\./, '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((segment) => {
      if (!segment) {
        return segment;
      }

      return segment.charAt(0).toLowerCase() + segment.slice(1);
    })
    .join('.');
};

const normalizeCollectionFieldPath = (fieldPath: string): string => {
  return fieldPath.replace(/\.\d+(?=\.|$)/g, '');
};

const resolveFieldLabel = (fieldPath: string): string => {
  const normalized = normalizeApiFieldPath(fieldPath);
  const collectionNormalized = normalizeCollectionFieldPath(normalized);

  if (OFFER_FIELD_LABELS[normalized]) {
    return OFFER_FIELD_LABELS[normalized];
  }

  if (OFFER_FIELD_LABELS[collectionNormalized]) {
    return OFFER_FIELD_LABELS[collectionNormalized];
  }

  if (collectionNormalized.startsWith('offerOptions')) {
    return 'Opcja oferty';
  }

  return normalized;
};

const translateOfferMessage = (message: string, fieldPath: string): string => {
  const trimmed = message.trim();
  const fieldLabel = resolveFieldLabel(fieldPath);

  if (/^The .* field is required\.?$/i.test(trimmed)) {
    return `Pole "${fieldLabel}" jest wymagane.`;
  }

  if (
    /^The value '.*' is (?:invalid|not valid for).*/i.test(trimmed) ||
    /could not be converted to/i.test(trimmed)
  ) {
    return `Pole "${fieldLabel}" ma nieprawidłową wartość.`;
  }

  if (/must be greater than 0/i.test(trimmed)) {
    return `Pole "${fieldLabel}" musi być większe od 0.`;
  }

  return trimmed;
};

const getErrorCode = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('errorCode' in data)) {
    return null;
  }

  const errorCode = (data as { errorCode?: unknown }).errorCode;
  if (typeof errorCode !== 'string' || !errorCode.trim()) {
    return null;
  }

  return errorCode.trim();
};

const getErrorStatus = (error: unknown): number | string | null => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return null;
  }

  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' || typeof status === 'string') {
    return status;
  }

  return null;
};

const mapGeneralMessage = (message: string, status: number | string | null): string => {
  const trimmed = message.trim();

  if (typeof status === 'number' && status >= 500) {
    return GENERIC_SERVER_ERROR_MESSAGE;
  }

  if (
    /^One or more validation errors occurred\.?$/i.test(trimmed) ||
    /The JSON value could not be converted/i.test(trimmed) ||
    /^Bad Request$/i.test(trimmed)
  ) {
    return GENERIC_VALIDATION_MESSAGE;
  }

  if (
    /^An unexpected error occurred\.?$/i.test(trimmed) ||
    /^Internal Server Error$/i.test(trimmed)
  ) {
    return GENERIC_SERVER_ERROR_MESSAGE;
  }

  return trimmed;
};

export const extractOfferEntityFormApiErrors = (
  error: unknown,
  fallbackMessage: string
): EntityFormApiErrorExtractionResult => {
  const validationErrors = extractValidationErrors(error);
  const fieldErrors: Record<string, string> = {};

  if (validationErrors) {
    Object.entries(validationErrors).forEach(([fieldName, messages]) => {
      const firstMessage = Array.isArray(messages) ? messages[0] : undefined;
      if (!firstMessage) {
        return;
      }

      const normalizedFieldName = normalizeApiFieldPath(fieldName);
      fieldErrors[normalizedFieldName] = translateOfferMessage(firstMessage, fieldName);
    });
  }

  if (Object.keys(fieldErrors).length > 0) {
    return {
      submitErrorMessage: GENERIC_VALIDATION_MESSAGE,
      fieldErrors,
    };
  }

  const errorCode = getErrorCode(error);
  if (errorCode && OFFER_ERROR_MESSAGES_BY_CODE[errorCode]) {
    return {
      submitErrorMessage: OFFER_ERROR_MESSAGES_BY_CODE[errorCode],
      fieldErrors,
    };
  }

  const apiMessage = extractApiErrorMessage(error, fallbackMessage);
  const status = getErrorStatus(error);
  return {
    submitErrorMessage: mapGeneralMessage(apiMessage || fallbackMessage, status),
    fieldErrors,
  };
};

